import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalysisStore } from '../../../../../stores/appAnalysisStore';
import { suggestWasteActionItems } from '../../../../../services/analysis/analysisService';
import { buildWasteActionPayload, WasteActionPeriod, WasteFinding } from '../../../../../utils/analysis/wasteActionPayload';
import { UtilizationProductRow } from '../../../../../utils/reports/utilizationReport';

export interface WasteActionRunInput {
  restaurantId: string;
  selectedKeys: string[];
  rows: UtilizationProductRow[];
  period: WasteActionPeriod;
  wastePercentage: number;
}

export type WasteActionRunError = 'grouping' | 'request' | 'allFailed';

/**
 * Utilization → AI waste action items, no review step: group the selection (money-savers
 * alternative groups), ask the server, save every ok group as a finding and open
 * /analysis/waste, where a super admin edits the action items.
 */
export function useWasteActionRun() {
  const navigate = useNavigate();
  const loadMoneySavers = useAnalysisStore(s => s.loadMoneySavers);
  const saveWasteFindings = useAnalysisStore(s => s.saveWasteFindings);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<WasteActionRunError | null>(null);
  const controller = useRef<AbortController | null>(null);

  useEffect(() => () => controller.current?.abort(), []);

  const run = async ({ restaurantId, selectedKeys, rows, period, wastePercentage }: WasteActionRunInput) => {
    if (running) return;
    const request = new AbortController();
    controller.current = request;
    setRunning(true);
    setError(null);
    try {
      await loadMoneySavers(restaurantId);
      const moneySavers = useAnalysisStore.getState().moneySaversBySelection[restaurantId];
      if (!moneySavers) { setError('grouping'); return; }
      const groups = buildWasteActionPayload(selectedKeys, rows, moneySavers.groupByKey, moneySavers.groupNames);
      if (!groups.length) { setError('allFailed'); return; }

      const response = await suggestWasteActionItems({ restaurantId, groups, period, wastePercentage }, request.signal);
      if (request.signal.aborted) return;
      const savedAt = new Date().toISOString();
      const findings: WasteFinding[] = groups.flatMap(group => {
        const result = response.results.find(entry => entry.groupId === group.groupId && entry.status === 'ok');
        return result ? [{
          ...group, restaurantId, period, savedAt,
          actionItems: result.items.map(item => ({ ...item, id: crypto.randomUUID() })),
        }] : [];
      });
      if (!findings.length) { setError('allFailed'); return; }
      saveWasteFindings(restaurantId, findings);
      navigate('/analysis/waste');
    } catch (failure) {
      if (request.signal.aborted) return;
      console.error('Failed to suggest waste action items', failure);
      setError('request');
    } finally {
      if (!request.signal.aborted) setRunning(false);
    }
  };

  return { run, running, error };
}
